<template>
    <div id="today-view__content"
         data-component="TodayViewListLayout">
        <TodayViewHourList v-show="showHoursList"
                           :hours="sessionHours"
                           :current="currentHour"
                           :loading="isLoading"
                           @select-hour="onSelectHour" />

        <div id="today-view__content-right"
             ref="content-right"
             @scroll="onRightScroll">
            <PageLoadingSpinner v-if="isLoading"></PageLoadingSpinner>
            <template v-else-if="sessionCountBeforeFilters < 1">
                <NoResults icon="fa-regular fa-calendar-xmark"
                           lang="TODAY_VIEW.NO_SESSIONS_BEFORE_FILTERS"></NoResults>
            </template>
            <template v-else-if="sessionCount < 1">
                <NoResults icon="fa-regular fa-filter-slash"
                           lang="TODAY_VIEW.NO_SESSIONS_AFTER_FILTERS"></NoResults>
            </template>
            <template v-else>
                <div id="today-view-sessions">
                    <TodayViewSession v-for="session in sessions"
                                      :id="`today-view-session-${session.ID}`"
                                      :ref="`today-view-session-${session.ID}`"
                                      :key="session.ID"
                                      :session="session"
                                      :event="events[session.eventID]"
                                      :availability="sessionAvailabilities[session.ID]" />
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import NoResults from '@/vue/components/NoResults';
import PageLoadingSpinner from '@/vue/components/PageLoadingSpinner';
import TodayViewHourList from './components/TodayViewHourList';
import TodayViewSession from './components/TodayViewSession';

export default {
    name: 'TodayViewListLayout',

    components: {
        PageLoadingSpinner,
        NoResults,
        TodayViewHourList,
        TodayViewSession,
    },

    props: {
        sessions: {
            type: Array,
            default: null,
        },
        events: {
            type: Object,
            default: () => ({}),
        },
        sessionHours: {
            type: Object,
            default: () => ({}),
        },
        sessionAvailabilities: {
            type: Object,
            default: () => ({}),
        },
        sessionCount: {
            type: Number,
            default: 0,
        },
        sessionCountBeforeFilters: {
            type: Number,
            default: 0,
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        showHoursList: {
            type: Boolean,
            default: false,
        },
        currentHour: {
            type: Number,
            default: null,
        },
    },

    emits: ['select-hour', 'right-scroll'],

    methods: {
        onSelectHour(hour) {
            this.$emit('select-hour', hour);
        },

        onRightScroll() {
            this.$emit('right-scroll');
        },

        jumpToSession(session) {
            const targetSessionEl = document.getElementById(`today-view-session-${session.ID}`);
            if (!targetSessionEl) {
                return;
            }

            // Take off 15px to give a little padding and make it clear that there is more above the element.
            const offset = Math.max(targetSessionEl.offsetTop - 15, 0);

            if (this.$refs['content-right']) {
                this.$refs['content-right'].scrollTop = offset;
            }
        },

        /**
         * @return {Session|null}
         */
        determineFirstVisibleSession() {
            if (!(this.sessions instanceof Array)) {
                return null;
            }

            // If the user has navigated away the 'content-right' may no longer exist when this method is called
            if (!this.$refs['content-right']) {
                return null;
            }

            // Get scroll position of the right section.
            const scrollPos = this.$refs['content-right'].scrollTop;

            const oneSessionHeight = 81;
            const sessionsScrolled = Math.floor(Math.round(scrollPos / oneSessionHeight));

            /**
             * @var {Session}
             */
            const firstSession = this.sessions[sessionsScrolled];

            return firstSession;
        },
    },
};
</script>

<style lang="less">
@import "../../../../less/variables";

#today-view__content {
    flex-grow: 1;
    flex-shrink: 1;
    overflow: auto;

    display: flex;
}

#today-view__content-right {
    flex-grow: 1;
    flex-shrink: 1;

    // If this has a relative position, when we check `offsetTop` on child element (sessions) it will be
    // relative to this element.
    position: relative;

    // Using scroll instead of auto so a scrollbar is always visible (depending on the device) and the width
    // of the page doesn't change depending on the day selected.
    overflow-y: scroll;
    scroll-behavior: smooth;
}

@media (max-width: @mobile-max) {
    #today-view-hour-list {
        display: none;
    }
}

@media (min-width: @non-mobile-min) {
    #today-view-sessions {
        // Add padding to the bottom of the list so that it becomes possible to scroll and have the last session
        // at the top of the screen.
        // I tried using 100% but it didn't work well, so it is:
        // 100dvh
        // minus the height of the top nav bar
        // minus the height of the filter bar on the sessions page (on desktop there is only one)
        // minus the height of one session
        // minus 15px so you see a little bit of the penultimate session.
        padding-bottom: ~"calc(100dvh - @{menu-bar-height} - @{filter-bar-height} - 81px - 15px)";
    }
}
</style>
